import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  Paper,
  Grid,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api, { fileApi } from '../services/api';

export interface UploadedFile {
  id: number;
  filename: string;
  file_type: string;
  file_size: number;
  url: string;
  thumbnail_url?: string;
  uploaded_by_id?: number;
  created_at: string;
}

interface FileUploadProps {
  taskId?: number;
  messageId?: number;
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // в байтове
  onUploadComplete?: (files: UploadedFile[]) => void;
  onError?: (error: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  taskId,
  messageId,
  multiple = false,
  accept = 'image/*,application/pdf,.doc,.docx',
  maxSize = 10 * 1024 * 1024, // 10 MB по подразбиране
  onUploadComplete,
  onError,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const validateFile = (file: File): boolean => {
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(2);
      const errorMsg = t('file_upload.error.file_too_large', { size: sizeMB });
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return false;
    }
    return true;
  };

  const uploadFile = async (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (taskId) formData.append('task_id', taskId.toString());
    if (messageId) formData.append('message_id', messageId.toString());

    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      const response = await api.post<UploadedFile>(
        '/api/v1/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setProgress(percentCompleted);
          },
        }
      );

      const uploadedFile = response.data;
      setUploadedFiles((prev) => [...prev, uploadedFile]);
      
      if (onUploadComplete) {
        onUploadComplete([uploadedFile]);
      }

      setProgress(100);
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 500);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('file_upload.error.upload_failed');
      setError(errorMsg);
      if (onError) onError(errorMsg);
      setUploading(false);
      setProgress(0);
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    if (!multiple && fileArray.length > 1) {
      setError(t('file_upload.error.single_file_only'));
      return;
    }

    fileArray.forEach((file) => {
      uploadFile(file);
    });
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      await fileApi.deleteFile(fileId);
      
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('file_upload.error.delete_failed');
      setError(errorMsg);
      if (onError) onError(errorMsg);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isImage = (fileType: string) => fileType.startsWith('image/');

  return (
    <Box>
      <Paper
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'grey.300',
          backgroundColor: dragActive ? 'action.hover' : 'background.paper',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input
          type="file"
          id="file-upload"
          multiple={multiple}
          accept={accept}
          onChange={handleChange}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        
        <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('file_upload.drag_or_click')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('file_upload.supported_formats')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('file_upload.max_size', { size: (maxSize / (1024 * 1024)).toFixed(0) })}
          </Typography>
        </label>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('file_upload.uploading', { progress })}
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {uploadedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('file_upload.uploaded_files', { count: uploadedFiles.length })}
          </Typography>
          <Grid container spacing={2}>
            {uploadedFiles.map((file) => (
              <Grid item xs={12} sm={6} md={4} key={file.id}>
                <Paper sx={{ p: 2, position: 'relative' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {isImage(file.file_type) ? (
                      <Box
                        component="img"
                        src={file.thumbnail_url || file.url}
                        alt={file.filename}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <FileIcon sx={{ fontSize: 60, color: 'action.active' }} />
                    )}
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={file.filename}>
                        {file.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.file_size)}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFile(file.id)}
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload; 